import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { ChemicalLabel } from '../data-models/chemical-label.model';
import { Chemical } from '../data-models/chemical.model';
import { ChemicalCreateRequest } from '../request-models/chemical-create-request.model';
import { ChemicalFilters } from '../request-models/chemical-filters.model';
import { ChemicalImportRow } from '../request-models/chemical-import-row.model';
import { ChemicalLabelRequest } from '../request-models/chemical-label-request.model';
import { ChemicalUpdateRequest } from '../request-models/chemical-update-request.model';
import { ChemicalImportJobResponse } from '../response-models/chemical-import-job-response.model';
import { Page } from '../response-models/page.model';
import { toChemicalRequest, toCreateChemicalRequest } from '../utils/chemical-request-utils';
import { isRecord } from '../utils/is-record';
import { countOrZero, idString, nullableString } from '../utils/wire-value-utils';

/**
 * ADN Recommended chemicals: `/admin/chemicals` (hub `admin-chemicals-controller.ts`), paged with `limit`.
 * Also the CSV import (queued; progress arrives over the socket) and the label sub-routes. EPA chemicals have
 * their own service, and logo uploads go through `FileUploadService`.
 */
@Injectable({ providedIn: 'root' })
export class ChemicalsService extends BaseService<Chemical> {
  constructor() {
    super('admin/chemicals', Chemical);
  }

  /** GET /admin/chemicals: one page of ADN Recommended chemicals. Blank filters are not sent. */
  listChemicals(filters: ChemicalFilters = {}): Observable<Page<Chemical>> {
    return this.getPage({ query: toListQuery(filters) });
  }

  /** GET /admin/chemicals/:id with its labels. Emits `null` when the hub returns no data. */
  getChemical(id: string): Observable<Chemical | null> {
    return this.get(id);
  }

  /** POST /admin/chemicals with only the writable fields that are set. */
  createChemical(fields: ChemicalCreateRequest): Observable<Chemical | null> {
    return this.create(toCreateChemicalRequest(fields));
  }

  /** PUT /admin/chemicals/:id with only the writable fields that are set. */
  updateChemical(id: string, fields: ChemicalUpdateRequest): Observable<Chemical | null> {
    return this.update(id, toChemicalRequest(fields));
  }

  /** DELETE /admin/chemicals/:id (soft delete: the hub archives it). */
  archiveChemical(id: string): Observable<void> {
    return this.delete(id);
  }

  /** GET /admin/chemicals/import-template: the CSV template as a Blob. */
  getImportTemplate(): Observable<Blob> {
    return this.download({ path: 'import-template' });
  }

  /**
   * POST /admin/chemicals/import: queues the import and returns the job. Progress arrives over the socket
   * (`chemical-import:progress`, `:complete`, `:failed`). EPA enrichment defaults to on.
   */
  importChemicals(rows: ChemicalImportRow[], options?: { enrichFromEpa?: boolean }): Observable<ChemicalImportJobResponse> {
    const body = { rows, enrichFromEpa: options?.enrichFromEpa ?? true };
    return this.request('POST', { path: 'import' }, toImportJob, body);
  }

  /** GET /admin/chemicals/import/:jobId/status: the queue's job info, as sent. */
  getImportJobStatus(jobId: string): Observable<unknown> {
    return this.request('GET', { path: ['import', jobId, 'status'] }, data => data ?? null);
  }

  /** POST /admin/chemicals/:id/labels. Emits `null` when the hub returns no label. */
  addLabel(chemicalId: string, label: ChemicalLabelRequest): Observable<ChemicalLabel | null> {
    const body = { labelUrl: label.labelUrl, labelName: label.labelName, acceptedDate: label.acceptedDate, setAsCurrent: label.setAsCurrent };
    return this.request('POST', { id: chemicalId, path: 'labels' }, toLabelOrNull, body);
  }

  /** DELETE /admin/chemicals/:id/labels/:labelId */
  deleteLabel(chemicalId: string, labelId: string): Observable<void> {
    return this.request('DELETE', { id: chemicalId, path: ['labels', labelId] }, () => undefined);
  }

  /** PUT /admin/chemicals/:id/labels/:labelId/set-current: toggles the label; returns it and all labels. */
  setCurrentLabel(chemicalId: string, labelId: string): Observable<{ label: ChemicalLabel | null; labels: ChemicalLabel[] }> {
    return this.request('PUT', { id: chemicalId, path: ['labels', labelId, 'set-current'] }, toLabelToggle, {});
  }
}

function toListQuery(filters: ChemicalFilters): Record<string, string | number | boolean | undefined> {
  return {
    page: filters.page,
    limit: filters.limit,
    query: filters.query?.trim() || undefined,
    category: filters.category || undefined,
    signalWord: filters.signalWord || undefined,
    featured: filters.featured ?? undefined,
    sponsor: filters.sponsor ?? undefined,
  };
}

function toImportJob(data: unknown): ChemicalImportJobResponse {
  const source = isRecord(data) ? data : {};
  return {
    jobId: idString(source['jobId']),
    status: 'queued',
    message: nullableString(source['message']) ?? '',
    totalRows: countOrZero(source['totalRows']),
  };
}

function toLabelOrNull(data: unknown): ChemicalLabel | null {
  return isRecord(data) ? new ChemicalLabel(data) : null;
}

function toLabelToggle(data: unknown): { label: ChemicalLabel | null; labels: ChemicalLabel[] } {
  const source = isRecord(data) ? data : {};
  const labels = source['labels'];
  return {
    label: toLabelOrNull(source['label']),
    labels: Array.isArray(labels) ? labels.map(item => new ChemicalLabel(item)) : [],
  };
}
